import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/apiService';
import { queryKeys } from '../config/queryClient';
import {
  AddFavouriteRequest,
  FavouriteCreatedResponse,
  FavouriteItem,
  OwnershipScope,
} from '../types/favourites';

type FavouritesListCache = FavouriteItem[] | undefined;

interface FavouritesMutationContext {
  readonly previousList: FavouritesListCache;
}

const favouritesListKey = queryKeys.favourites.list();

const normalizeIsin = (isin: string): string => isin.trim().toUpperCase();

const toFavouriteItem = (favourite: FavouriteCreatedResponse): FavouriteItem => ({
  id: favourite.id,
  proId: favourite.proId,
  isin: normalizeIsin(favourite.isin),
  createdAt: favourite.createdAt,
  updatedAt: favourite.updatedAt,
});

/**
 * Shared favourites list cache — Portfolio and Detail both read this query key,
 * so optimistic updates sync the star icons across screens without a manual refresh.
 */
const syncFavouriteIntoListCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  favourite: FavouriteItem
): void => {
  const normalizedIsin = normalizeIsin(favourite.isin);

  queryClient.setQueryData<FavouriteItem[]>(favouritesListKey, (current) => {
    const withoutCurrent = (current ?? []).filter(
      (item) => normalizeIsin(item.isin) !== normalizedIsin
    );
    return [favourite, ...withoutCurrent];
  });
};

const removeFavouriteFromListCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  isin: string
): void => {
  const normalizedIsin = normalizeIsin(isin);

  queryClient.setQueryData<FavouriteItem[]>(favouritesListKey, (current) =>
    (current ?? []).filter((item) => normalizeIsin(item.isin) !== normalizedIsin)
  );
};

const invalidateWatchlists = (queryClient: ReturnType<typeof useQueryClient>) => {
  return queryClient.invalidateQueries({ queryKey: queryKeys.favourites.watchlists() });
};

const invalidateFavouritesQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  return queryClient.invalidateQueries({ queryKey: queryKeys.favourites.all });
};

/**
 * List favourites (ids / isins) for the authenticated user.
 * Same query key is used by Portfolio rows and Product Detail.
 */
export const useFavourites = () => {
  return useQuery({
    queryKey: favouritesListKey,
    queryFn: () => apiService.getFavourites(),
    staleTime: 60 * 1000,
    // Ensure remounted screens pick up any cache writes from the other screen
    refetchOnMount: true,
  });
};

/**
 * Watchlist with market snapshot (bid/ask/name/currency/issuer).
 */
export const useFavouritesWatchlist = (ownershipScope: OwnershipScope = 'group') => {
  return useQuery({
    queryKey: queryKeys.favourites.watchlist(ownershipScope),
    queryFn: () => apiService.getFavouritesWatchlist(ownershipScope),
    staleTime: 30 * 1000,
    refetchOnMount: true,
  });
};

/**
 * Whether an ISIN is in the current favourites list (from shared React Query cache).
 */
export const useIsFavourite = (isin: string) => {
  const { data: favourites, ...rest } = useFavourites();
  const normalizedIsin = normalizeIsin(isin);
  const isFavourite = Boolean(
    favourites?.some((favourite) => normalizeIsin(favourite.isin) === normalizedIsin)
  );

  return {
    ...rest,
    data: favourites,
    isFavourite,
  };
};

/**
 * Add product to favourites with optimistic list-cache sync (Portfolio ↔ Detail).
 */
export const useAddFavourite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: AddFavouriteRequest): Promise<FavouriteCreatedResponse> =>
      apiService.addFavourite({
        ...request,
        isin: normalizeIsin(request.isin),
      }),
    onMutate: async (request): Promise<FavouritesMutationContext> => {
      await queryClient.cancelQueries({ queryKey: favouritesListKey });

      const previousList = queryClient.getQueryData<FavouriteItem[]>(favouritesListKey);
      const normalizedIsin = normalizeIsin(request.isin);
      const alreadyFavourite = previousList?.some(
        (item) => normalizeIsin(item.isin) === normalizedIsin
      );

      if (!alreadyFavourite) {
        const optimisticFavourite: FavouriteItem = {
          id: -Date.now(),
          proId: 0,
          isin: normalizedIsin,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        syncFavouriteIntoListCache(queryClient, optimisticFavourite);
      }

      return { previousList };
    },
    onError: (_error, _request, context) => {
      if (context) {
        queryClient.setQueryData(favouritesListKey, context.previousList);
      }
    },
    onSuccess: (favourite) => {
      syncFavouriteIntoListCache(queryClient, toFavouriteItem(favourite));
    },
    onSettled: async () => {
      await invalidateWatchlists(queryClient);
      await queryClient.invalidateQueries({ queryKey: favouritesListKey });
    },
  });
};

/**
 * Remove product from favourites with optimistic list-cache sync (Portfolio ↔ Detail).
 */
export const useRemoveFavourite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (isin: string): Promise<void> =>
      apiService.removeFavourite(normalizeIsin(isin)),
    onMutate: async (isin): Promise<FavouritesMutationContext> => {
      await queryClient.cancelQueries({ queryKey: favouritesListKey });

      const previousList = queryClient.getQueryData<FavouriteItem[]>(favouritesListKey);
      removeFavouriteFromListCache(queryClient, isin);

      return { previousList };
    },
    onError: (_error, _isin, context) => {
      if (context) {
        queryClient.setQueryData(favouritesListKey, context.previousList);
      }
    },
    onSettled: async () => {
      await invalidateWatchlists(queryClient);
      await queryClient.invalidateQueries({ queryKey: favouritesListKey });
    },
  });
};

/**
 * Manual invalidation helpers for favourites cache.
 */
export const useInvalidateFavourites = () => {
  const queryClient = useQueryClient();

  return {
    invalidateAll: () => invalidateFavouritesQueries(queryClient),
    invalidateList: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.favourites.lists() }),
    invalidateWatchlist: () => invalidateWatchlists(queryClient),
  };
};
